/**
 * Chairside client-side storage — Preview only.
 *
 * All state stored here is UI hint state: pinned case IDs, "last opened
 * case" pointer, timer running-state, and per-session workflow analytics
 * session ID. NO PHI, NO free-text clinician notes, NO patient identifiers
 * beyond the opaque case_id (which is a UUID/hex string and carries no
 * clinical meaning on its own).
 *
 * The authenticated backend remains the system of record. Every UI-hint
 * value can be recomputed from the backend on demand — losing localStorage
 * degrades the UX but never corrupts clinical data.
 */

const NS = "prosthoai.chairside.v1";

const KEYS = {
  favorites: `${NS}.favorites`,          // string[] of case IDs, max 5
  lastCase: `${NS}.lastCase`,            // { id, at (iso), snippet? }
  timer: `${NS}.timer`,                  // { case_id, started_at (ms) }
  session: `${NS}.session`,              // { id, started_at (ms) }
};

const MAX_FAVORITES = 5;

// ─── Safe JSON accessors ──────────────────────────────────────────────────
function _get(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    return JSON.parse(raw);
  } catch (_) {
    return fallback;
  }
}
function _set(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (_) { /* quota / privacy mode → drop silently */ }
}
function _del(key) {
  try { localStorage.removeItem(key); } catch (_) { /* ignore */ }
}

// ─── Favorites (pinned cases) ─────────────────────────────────────────────
export function getFavoriteCaseIds() {
  const v = _get(KEYS.favorites, []);
  return Array.isArray(v) ? v.slice(0, MAX_FAVORITES) : [];
}
export function isFavoriteCase(caseId) {
  if (!caseId) return false;
  return getFavoriteCaseIds().includes(caseId);
}
export function togglePinCase(caseId) {
  if (!caseId) return { pinned: false, ids: [] };
  const cur = getFavoriteCaseIds();
  if (cur.includes(caseId)) {
    const next = cur.filter((x) => x !== caseId);
    _set(KEYS.favorites, next);
    return { pinned: false, ids: next };
  }
  if (cur.length >= MAX_FAVORITES) {
    return { pinned: false, ids: cur, limit: true };
  }
  // Newest pins first
  const next = [caseId, ...cur].slice(0, MAX_FAVORITES);
  _set(KEYS.favorites, next);
  return { pinned: true, ids: next };
}
export const FAVORITES_LIMIT = MAX_FAVORITES;

// ─── Last case (Resume Last Case) ─────────────────────────────────────────
export function rememberLastCase(caseId, extras = {}) {
  if (!caseId) return;
  _set(KEYS.lastCase, {
    id: caseId,
    at: new Date().toISOString(),
    // Optional non-PHI hint only. Callers should pass status/date, never
    // clinician notes or presenting concern.
    ...(extras.status ? { status: String(extras.status).slice(0, 40) } : {}),
    ...(extras.created_at ? { created_at: String(extras.created_at).slice(0, 40) } : {}),
  });
}
export function getLastCase() {
  const v = _get(KEYS.lastCase, null);
  if (!v || typeof v !== "object" || !v.id) return null;
  return v;
}
export function clearLastCase() { _del(KEYS.lastCase); }

// ─── Chairside timer ──────────────────────────────────────────────────────
export function startChairsideTimer(caseId) {
  if (!caseId) return;
  _set(KEYS.timer, { case_id: caseId, started_at: Date.now() });
}
export function getChairsideTimer() {
  const v = _get(KEYS.timer, null);
  if (!v || !v.case_id || !v.started_at) return null;
  return v;
}
export function stopChairsideTimer() {
  const t = getChairsideTimer();
  _del(KEYS.timer);
  if (!t) return null;
  return { case_id: t.case_id, duration_ms: Date.now() - t.started_at };
}

// ─── Session (for workflow analytics) ─────────────────────────────────────
export function getOrCreateAnalyticsSession() {
  const existing = _get(KEYS.session, null);
  if (existing && existing.id && existing.started_at &&
      Date.now() - existing.started_at < 8 * 60 * 60 * 1000) {   // 8h sliding
    return existing;
  }
  // Opaque session id — hex, no PII.
  const id = (crypto?.randomUUID && crypto.randomUUID().replace(/-/g, "")) ||
    Math.random().toString(36).slice(2) + Date.now().toString(36);
  const rec = { id, started_at: Date.now() };
  _set(KEYS.session, rec);
  return rec;
}
export function clearAnalyticsSession() { _del(KEYS.session); }

// ─── Nuke everything (called on logout) ───────────────────────────────────
export function clearAllChairsideState() {
  Object.values(KEYS).forEach(_del);
}

/**
 * Local-storage schema (v1):
 *   prosthoai.chairside.v1.favorites : string[]  case IDs, max 5, newest-first
 *   prosthoai.chairside.v1.lastCase  : { id, at, status?, created_at? }
 *   prosthoai.chairside.v1.timer     : { case_id, started_at (ms) }
 *   prosthoai.chairside.v1.session   : { id, started_at (ms) }
 *
 * Contract: no free-text, no patient identifiers beyond opaque IDs, no
 * clinician notes. Bump the `v1` suffix to migrate.
 */
